import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

// Module-level memoised cache — persists across mounts so navigating back to home
// is instant. Cache TTL: 5 minutes (banners change rarely).
let _cache = { data: null, fetchedAt: 0 };
const TTL_MS = 5 * 60 * 1000;

export function useHomeBanners({ admin = false } = {}) {
  const [banners, setBanners] = useState(() => (!admin && _cache.data && Date.now() - _cache.fetchedAt < TTL_MS) ? _cache.data : []);
  const [loading, setLoading] = useState(!banners.length);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = admin ? "/admin/home-banners" : "/home-banners";
      const r = await api.get(url);
      setBanners(r.data || []);
      if (!admin) _cache = { data: r.data || [], fetchedAt: Date.now() };
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [admin]);

  useEffect(() => {
    if (admin || !_cache.data || Date.now() - _cache.fetchedAt > TTL_MS) {
      refresh();
    } else {
      setLoading(false);
    }
  }, [admin, refresh]);

  return { banners, loading, error, refresh };
}

export function invalidateHomeBannersCache() {
  _cache = { data: null, fetchedAt: 0 };
}

/** Resolve a banner's `link_type` + `link_value` to an actual href. */
export function bannerHref(b) {
  if (!b) return null;
  const v = (b.link_value || "").trim();
  switch (b.link_type) {
    case "product":  return v ? `/marketplace?product=${encodeURIComponent(v)}` : "/marketplace";
    case "category": return v ? `/marketplace?cat=${encodeURIComponent(v)}` : "/marketplace";
    case "event":    return v ? `/events?id=${encodeURIComponent(v)}` : "/events";
    case "campaign": return v ? `/community?campaign=${encodeURIComponent(v)}` : "/community";
    case "external": return /^https?:\/\//i.test(v) ? v : null;
    case "internal": return v && v.startsWith("/") ? v : null;
    case "none":
    default:         return null;
  }
}
